import { Link } from '@tanstack/react-router'
import { Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { formatCount } from '../format'
import { useUsageSummary } from '../queries'

/**
 * Indicador compacto do saldo no topbar (DL-32). Visível a todo membro, leva a Planos e uso. Neutro no normal,
 * âmbar com saldo baixo, vermelho com o agente pausado. Carregando ou com erro: não aparece (nunca um "0" falso).
 */
export function CreditsIndicator({
  tenantId,
  tenantSlug,
}: {
  readonly tenantId: string
  readonly tenantSlug: string
}) {
  const { t, i18n } = useTranslation()
  const summary = useUsageSummary(tenantId)

  if (summary.data === undefined) {
    return null
  }

  const { available, pausedForCredits, lowBalance } = summary.data
  const count = formatCount(Math.max(0, available), i18n.language)

  return (
    <Link
      to="/app/$tenantSlug/billing"
      params={{ tenantSlug }}
      data-testid="credits-indicator"
      aria-label={
        pausedForCredits
          ? t('billing.topbar.pausedAriaLabel')
          : t('billing.topbar.ariaLabel', { value: count })
      }
      className={cn(
        'flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
        pausedForCredits
          ? 'border-status-danger/30 bg-status-danger-subtle text-status-danger'
          : lowBalance
            ? 'border-status-warning/30 bg-status-warning-subtle text-status-warning'
            : 'border-border-default bg-background-subtle text-foreground-secondary hover:text-foreground-primary',
      )}
    >
      <Zap aria-hidden="true" className="h-3.5 w-3.5" />
      {pausedForCredits ? (
        <span>{t('billing.topbar.paused')}</span>
      ) : (
        <span>
          {count} <span className="hidden sm:inline">{t('billing.interactions')}</span>
        </span>
      )}
    </Link>
  )
}
